import { useState, useEffect, useRef, useCallback } from 'react';
import { getFirestore, doc, updateDoc, setDoc } from 'firebase/firestore';
import { toCurrencyInputString, parseCurrencyInput } from '../utils/currencyInput';

// Owns every moving part of the calculator's asking-price field: the displayed
// string, the blur-to-persist write, the wholesaler-original-price reset, the
// (edited) indicator, the error sublabel, and the blur-vs-reset race guard.
//
// Re-hydrates from `lead` whenever leadDocId changes (new lead loaded). Guarded
// by a ref so optimistic setLead updates from this hook's own writes don't
// re-trigger hydration and clobber in-progress user input.
//
// Reset target prefers the wholesaler's first-stated price (entries[0]) so the
// affordance restores the source-of-truth even before the first persist has
// captured originalAskingPrice on the doc.
//
// `currentLeadDocIdRef` is owned by useLeadFetch and shared across all hooks
// + page callbacks that need the mid-flight nav guard, so a single source of
// truth tracks "what lead is loaded right now."
//
// `discountDefaultPrice` (optional) — when the caller passes a number, the field
// hydrates to THAT instead of the real asking price (still only in the local
// calculator state; never written to `lead.currentAskingPrice` unless the user
// blurs after actually editing it, since `persistedAskingPriceRef` is seeded
// with the same value, so an untouched blur no-ops). `isDiscountDefault`
// tells the caller the field is still showing that override (not a real edit),
// so it can use different copy than "edited from wholesaler's stated price."
//
// ✏️ This hook is deliberately AGNOSTIC about which number it is handed — it only
// distinguishes "our default" from "the real ask" from "the operator's edit". The one
// caller is the co-living ledger, which passed `offerRecommendation.offerPrice` (the
// DOM/price-cut discount forecast) until 2026-08-10 and now passes the **MAO**
// (`coLivingCashOutRank.mao`). The flip/rental ledgers never pass it and always want
// the real ask.
// 🪤 The `discountDefaultPrice` / `isDiscountDefault` names are left over from that first
// caller and now under-describe the thing — read them as "caller-supplied default",
// not as anything to do with a discount. Renaming touches this hook, LeadDetailV2 and
// two test files, so it is recorded here rather than done in passing.
export function useAskingPriceField({ companyId, leadDocId, propertyId, lead, setLead, currentLeadDocIdRef, discountDefaultPrice = null }) {
    const [value, setValue] = useState('');
    const [error, setError] = useState(false);
    const persistedAskingPriceRef = useRef(null);
    const initialAskingPriceRef = useRef(null);
    // The rounded discount-default value this hydration used, or null when this
    // hydration used the real ask. Read only to compute `isDiscountDefault`.
    const discountDefaultValueRef = useRef(null);
    // True the instant the operator interacts with this field (types or resets)
    // since the last hydration — reset to false only by a fresh hydration.
    // Deliberately NOT inferred from comparing numbers (e.g. "does the field
    // still equal persistedAskingPriceRef.current"): a SAVED edit updates both
    // the local ref and the upstream lead doc to the same new value, so a
    // numeric comparison can't tell "never touched" from "touched, saved, and
    // the source caught up" — only an explicit flag can.
    const touchedSinceHydrationRef = useRef(false);
    // Set true synchronously inside onReset so the deferred blur handler
    // short-circuits and we don't issue a redundant Firestore write with the
    // still-edited value before the reset write lands.
    const justResetRef = useRef(false);
    const lastHydratedLeadDocIdRef = useRef(null);

    useEffect(() => {
        if (!leadDocId || !lead) return;
        if (lastHydratedLeadDocIdRef.current === leadDocId) return;
        lastHydratedLeadDocIdRef.current = leadDocId;
        const realAsk = lead.currentAskingPrice ?? null;
        const useDiscountDefault = typeof discountDefaultPrice === 'number'
            && Number.isFinite(discountDefaultPrice)
            && realAsk != null
            && Math.round(discountDefaultPrice) !== Math.round(realAsk);
        const ask = useDiscountDefault ? Math.round(discountDefaultPrice) : realAsk;
        const init =
            lead.originalAskingPrice
            ?? lead.entries?.[0]?.askingPrice
            ?? lead.currentAskingPrice
            ?? null;
        setValue(toCurrencyInputString(ask));
        persistedAskingPriceRef.current = ask;
        initialAskingPriceRef.current = init;
        discountDefaultValueRef.current = useDiscountDefault ? ask : null;
        touchedSinceHydrationRef.current = false;
        setError(false);
    }, [leadDocId, lead, discountDefaultPrice]);

    // The hydration above is gated to run ONCE per leadDocId, so any LATER
    // change in what `discountDefaultPrice` says needs a separate path — while
    // the operator hasn't touched the field, this keeps it in sync with
    // whichever of {discount default, real ask} currently applies. Three cases,
    // one effect:
    //   - arrives late: a co-living lead was still mid-underwriting when this
    //     page opened; offerRecommendation lands once processing finishes and
    //     the query refetches.
    //   - is revoked: LeadDetailV2 shares this ONE field across three mutually
    //     exclusive ledgers (flip/co-living/rental) on the same leadDocId, and
    //     only passes discountDefaultPrice while the co-living ledger is
    //     showing. Running ARV on a co-living lead flips `showsCoLivingLedger`
    //     false LIVE, mid-session (no leadDocId change, no remount) — without
    //     this, the co-living discount price would keep sitting in the flip/
    //     rental ledger's asking-price field and silently skew its math.
    //   - recalculates to a different number: e.g. a re-ingestion.
    // Never fires once the operator has an opinion (touchedSinceHydrationRef).
    useEffect(() => {
        if (!leadDocId || !lead) return;
        if (lastHydratedLeadDocIdRef.current !== leadDocId) return; // let the hydration above own the first pass
        if (touchedSinceHydrationRef.current) return;
        const realAsk = lead.currentAskingPrice ?? null;
        if (realAsk == null) return; // nothing sane to fall back to
        const roundedRealAsk = Math.round(realAsk);
        const validDiscount = typeof discountDefaultPrice === 'number'
            && Number.isFinite(discountDefaultPrice)
            && Math.round(discountDefaultPrice) !== roundedRealAsk;
        const nextAsk = validDiscount ? Math.round(discountDefaultPrice) : roundedRealAsk;
        const nextDiscountValue = validDiscount ? nextAsk : null;
        if (nextAsk === persistedAskingPriceRef.current
            && nextDiscountValue === discountDefaultValueRef.current) return; // already in sync
        setValue(toCurrencyInputString(nextAsk));
        persistedAskingPriceRef.current = nextAsk;
        discountDefaultValueRef.current = nextDiscountValue;
    }, [leadDocId, lead, discountDefaultPrice]);

    // True only while the field still shows the untouched discount-default
    // value from hydration — false the moment the user types or resets away
    // from it, so it can never linger as a stale label on a real edit.
    const isDiscountDefault = (() => {
        const defaultVal = discountDefaultValueRef.current;
        if (defaultVal == null) return false;
        const cur = parseCurrencyInput(value);
        return cur != null && Math.round(cur) === defaultVal;
    })();

    // Edited = current input differs from the price we hydrated with. Computed
    // off the local ref (not lead.originalAskingPrice) so the indicator + reset
    // link appear on the first keystroke instead of after the first save.
    const isEdited = (() => {
        const cur = parseCurrencyInput(value);
        const init = initialAskingPriceRef.current;
        return init != null && cur != null && Math.round(cur) !== init;
    })();

    // Persist asking-price edits on blur. Mirrors LeadDetail's handler: writes
    // data.currentAskingPrice on the lead doc + captures data.originalAskingPrice
    // on first edit, AND mirrors to propertyData.calculatorInputs.purchasePrice
    // and propertyData.purchasePrice. The property-doc fields are what V1
    // PropertyLead reads first (LeadDetail.js:619 priority cascade); without
    // them, V1 silently reads stale values when V2 has edited the asking price.
    // Always rounds to integer to keep precision consistent.
    const persistAskingPrice = useCallback(async () => {
        if (!companyId || !leadDocId) return;
        const parsed = parseCurrencyInput(value);
        const next = parsed != null ? Math.round(parsed) : null;
        if (next === persistedAskingPriceRef.current) return;
        try {
            const db = getFirestore();
            const leadRef = doc(db, 'companies', companyId, 'leads', leadDocId);
            const updates = {
                'data.currentAskingPrice': next,
                'data.updatedAt': new Date(),
            };
            if (lead?.originalAskingPrice == null && lead?.currentAskingPrice != null) {
                updates['data.originalAskingPrice'] = lead.currentAskingPrice;
            }
            const writes = [updateDoc(leadRef, updates)];
            // Mirror to the property doc so V1's read priority cascade
            // (propertyCalculator.purchasePrice → propertyData.purchasePrice →
            // leadData.currentAskingPrice) sees the V2-edited value first.
            if (propertyId) {
                const propertyRef = doc(db, 'companies', companyId, 'properties', propertyId);
                writes.push(
                    setDoc(propertyRef, {}, { merge: true }).then(() =>
                        updateDoc(propertyRef, {
                            'data.calculatorInputs.purchasePrice': next,
                            'data.purchasePrice': next,
                            'data.updatedAt': new Date(),
                        })
                    )
                );
            }
            await Promise.all(writes);
            if (currentLeadDocIdRef.current !== leadDocId) return;
            setLead(prev => ({
                ...prev,
                currentAskingPrice: next,
                originalAskingPrice: prev?.originalAskingPrice ?? prev?.currentAskingPrice ?? null,
            }));
            persistedAskingPriceRef.current = next;
            setError(false);
        } catch (err) {
            console.error('Failed to persist asking price:', err);
            if (currentLeadDocIdRef.current === leadDocId) setError(true);
        }
    }, [companyId, leadDocId, propertyId, value, lead, setLead, currentLeadDocIdRef]);

    // Single blur handler: defers one tick so a click on the reset link gets
    // to set justResetRef.current first and we skip the redundant write. Also
    // reformats the displayed value (e.g. "70000" → "70,000").
    const onBlur = useCallback(() => {
        setTimeout(() => {
            if (justResetRef.current) {
                justResetRef.current = false;
                return;
            }
            const parsed = parseCurrencyInput(value);
            if (parsed != null) {
                setValue(toCurrencyInputString(Math.round(parsed)));
            }
            persistAskingPrice();
        }, 0);
    }, [value, persistAskingPrice]);

    const onReset = useCallback(async () => {
        const init = initialAskingPriceRef.current;
        if (init == null) return;
        // Mark first so the about-to-fire blur (from focus leaving the input)
        // skips its persist call.
        justResetRef.current = true;
        // A deliberate reset is a touch too — it must permanently rule out the
        // late-discount-adoption effect above for this hydration, the same as
        // typing does, even though it can land back on a number that
        // superficially looks "untouched."
        touchedSinceHydrationRef.current = true;
        setValue(toCurrencyInputString(init));
        if (!companyId || !leadDocId) return;
        try {
            const db = getFirestore();
            const leadRef = doc(db, 'companies', companyId, 'leads', leadDocId);
            const writes = [updateDoc(leadRef, {
                'data.currentAskingPrice': init,
                'data.updatedAt': new Date(),
            })];
            if (propertyId) {
                const propertyRef = doc(db, 'companies', companyId, 'properties', propertyId);
                writes.push(
                    setDoc(propertyRef, {}, { merge: true }).then(() =>
                        updateDoc(propertyRef, {
                            'data.calculatorInputs.purchasePrice': init,
                            'data.purchasePrice': init,
                            'data.updatedAt': new Date(),
                        })
                    )
                );
            }
            await Promise.all(writes);
            if (currentLeadDocIdRef.current !== leadDocId) return;
            setLead(prev => ({ ...prev, currentAskingPrice: init }));
            persistedAskingPriceRef.current = init;
            setError(false);
        } catch (err) {
            console.error('Failed to reset asking price:', err);
            if (currentLeadDocIdRef.current === leadDocId) setError(true);
        }
    }, [companyId, leadDocId, propertyId, setLead, currentLeadDocIdRef]);

    // Exposed setter marks the field touched — this is how the late-discount-
    // adoption effect above learns the operator started typing. Internal calls
    // (hydration, onReset, onBlur's reformat) use the raw `setValue` above and
    // deliberately bypass this, since those aren't operator edits.
    const setValueTouched = useCallback((next) => {
        touchedSinceHydrationRef.current = true;
        setValue(next);
    }, []);

    return {
        value,
        setValue: setValueTouched,
        isEdited,
        isDiscountDefault,
        error,
        onBlur,
        onReset,
        initialAskingPrice: initialAskingPriceRef.current,
    };
}
